import { useMemo } from 'react'
import { useQuery } from '@based/sdk/react'
import { navigate } from 'wouter/use-browser-location'
import { ItemListCard } from '@app/components/blocks/card/ItemListCard'
import { useTranslations } from '@i18n/useLanguage'
import { pages } from '@app/util/links'
import { HousingFormListRegionalModel } from 'functions/app/housing-form/list-regional'
import defaultHousingFormPicture from '@app/assets/defaultHousingFormPicture.png'

type Props = (
  | { regionId: number; municipalityId?: never }
  | { municipalityId: number; regionId?: never }
) & {
  spotlightedHousingForms: Omit<HousingFormListRegionalModel, 'followers'>[]
  entityName: string
}

export const RegionalHousingFormsCard = ({
  regionId,
  municipalityId,
  spotlightedHousingForms,
  entityName,
}: Props) => {
  const t = useTranslations().app
  const housingFormsQuery = useQuery('list-regional-housing-forms', {
    regionId,
    municipalityId,
    top: 5,
  })
  const housingForms = useMemo(
    () => (housingFormsQuery.data ?? []) as HousingFormListRegionalModel[],
    [housingFormsQuery.data],
  )

  const items = useMemo(() => {
    const spotlightedIds = new Set(spotlightedHousingForms.map((h) => h.id))
    const spotlightedSorted = [...spotlightedHousingForms].sort(
      (a, b) => ((a as { $order?: number }).$order ?? Infinity) - ((b as { $order?: number }).$order ?? Infinity),
    )
    const popularSorted = [...housingForms].sort(
      (a, b) => b.followers.count - a.followers.count,
    )

    return [
      ...spotlightedSorted,
      ...popularSorted.filter((hf) => !spotlightedIds.has(hf.id)),
    ]
      .slice(0, 5)
      .map((hf) => ({
        image: hf.images?.[0]?.src ?? defaultHousingFormPicture,
        title: hf.title,

        onClick: () => navigate(pages.housingForm(hf.slug)),
      }))
  }, [spotlightedHousingForms, housingForms, t.common.followersAmount])

  if (items.length === 0) return null

  return (
    <ItemListCard
      title={`${t.regionPage.popularHousingFormsIn} ${entityName}`}
      items={items}
    />
  )
}
